var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import styled from 'styled-components';
import { muiChevronLeft, muiChevronRight } from '@ally/metronome-icons';
import { PageBase } from './PageBase';
import { Icon } from '../Icon';
import { liStyles, ulStyles } from './styles';
import { middlePagesToShow, showLeftEllipsis, showRightEllipsis } from './utils';
import whisper from '../whisper';
import { ResultsPerPageBase } from './ResultsPerPageBase';
import { Divider } from './Divider';
// Theme value for 32px?
const StyledNav = styled.nav `
  height: 32px;
  display: flex;
  align-items: center;
`;
const ArrowAnchor = styled.a `
  display: flex;
  align-items: center;
  width: 24px;
  border: none;
  background-color: initial;
  margin-left: 0;
  margin-right: 0;
  height: 100%;
  cursor: pointer;
  text-decoration: none;
  ${({ theme: { colors }, $disabled }) => $disabled
    ? `
          path {
            fill: ${colors['slate-300']};
          }
      `
    : ''}
`;
const StyledLI = styled.li `
  ${liStyles}
`;
const ChevronLI = styled(StyledLI) `
  width: 24px;
  min-width: initial;
`;
const StyledEllipsis = styled.span `
  flex: 1;
  font-weight: normal;
  ${({ theme: { colors } }) => `color: ${colors['gray-900']}`}
`;
const Ellipsis = (_a) => {
    var others = __rest(_a, []);
    return (React.createElement(StyledLI, Object.assign({}, others),
        React.createElement(Divider, null),
        React.createElement(StyledEllipsis, { "aria-hidden": true }, "..."),
        React.createElement(Divider, null)));
};
// Chevrons
const StyledIcon = styled(Icon) `
  flex: 1;

  ${({ theme: { colors }, $disabled }) => `
    ${$disabled ? `path { fill: ${colors['slate-300']}; }` : 'cursor: pointer;'}
  `}
`;
const StyledUL = styled.ul `
  ${ulStyles}
`;
const Wrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ theme: { space } }) => `
    gap: ${space.lg};
  `}
`;
export const PaginationBase = ({ pageCount, currentPage, onChange, optionCount = 7, resultsPerPage, paginationName, className, }) => {
    if (optionCount < 5) {
        whisper.warn({
            message: `Pagination component has invalid optionCount prop. Value must be greater than or equal to 5. Defaulting to 7.`,
        });
    }
    const safeOptionCount = optionCount < 5 ? 7 : optionCount;
    const safePageCount = pageCount <= 0 ? 1 : Math.ceil(pageCount);
    const getPageBaseProps = (pageNumber) => ({
        pageNumber,
        currentPage: currentPage === pageNumber,
        onClick: () => onChange(pageNumber),
        ariaLabel: `Page ${pageNumber}`,
        'aria-current': currentPage === pageNumber ? 'page' : undefined,
    });
    const middlePages = middlePagesToShow(safePageCount, currentPage, safeOptionCount);
    return (React.createElement(Wrapper, { className: className },
        React.createElement(StyledNav, { "aria-label": `${paginationName} – Page ${currentPage} of ${pageCount}` },
            React.createElement(StyledUL, null,
                React.createElement(ChevronLI, null,
                    React.createElement(ArrowAnchor, { "aria-label": "Previous Page Link", "aria-disabled": currentPage === 1, tabIndex: currentPage === 1 ? -1 : 0, "data-testid": "mui-pagination-prev-page", "$disabled": currentPage === 1, onClick: (e) => {
                            e.preventDefault();
                            if (currentPage !== 1) {
                                onChange(currentPage - 1);
                            }
                        }, href: "", role: "link" },
                        React.createElement(StyledIcon, { icon: muiChevronLeft, size: "xs", "$disabled": currentPage === 1, "aria-hidden": true }))),
                React.createElement(PageBase, Object.assign({}, getPageBaseProps(1), { excludeLeftDivider: true, excludeRightDivider: safePageCount === 1, key: `${paginationName}-${1}` })),
                showLeftEllipsis(safePageCount, middlePages) && (React.createElement(Ellipsis, { "data-testid": "mui-pagination-left-ellipsis" })),
                !showLeftEllipsis(safePageCount, middlePages) &&
                    safePageCount >= 3 && (React.createElement(PageBase, Object.assign({}, getPageBaseProps(2), { key: `${paginationName}-${2}` }))),
                middlePages.map(pageNumber => (React.createElement(PageBase, Object.assign({}, getPageBaseProps(pageNumber), { key: `${paginationName}-${pageNumber}` })))),
                !showRightEllipsis(safePageCount, middlePages) &&
                    safePageCount >= 4 && (React.createElement(PageBase, Object.assign({}, getPageBaseProps(safePageCount - 1), { key: `${paginationName}-${safePageCount - 1}` }))),
                showRightEllipsis(safePageCount, middlePages) && (React.createElement(Ellipsis, { "data-testid": "mui-pagination-right-ellipsis" })),
                safePageCount !== 1 && (React.createElement(PageBase, Object.assign({}, getPageBaseProps(safePageCount), { excludeRightDivider: true, key: `${paginationName}-${safePageCount}` }))),
                React.createElement(ChevronLI, null,
                    React.createElement(ArrowAnchor, { "aria-label": "Next Page Link", "aria-disabled": currentPage === safePageCount, "data-testid": "mui-pagination-next-page", onClick: (e) => {
                            e.preventDefault();
                            if (currentPage !== safePageCount) {
                                onChange(currentPage + 1);
                            }
                        }, href: "", role: "link", tabIndex: currentPage === safePageCount ? -1 : 0, "$disabled": currentPage === safePageCount },
                        React.createElement(StyledIcon, { icon: muiChevronRight, size: "xs", "$disabled": currentPage === safePageCount, "aria-hidden": true }))))),
        !!resultsPerPage && (React.createElement(ResultsPerPageBase, { options: resultsPerPage.options, onChange: resultsPerPage.onChange, selected: resultsPerPage.selected }))));
};
//# sourceMappingURL=PaginationBase.js.map